import React from 'react'
import styled from '@emotion/styled'
import { useIntl } from 'react-intl'
import { COURSE_TYPES, VIDEO_COURSE_TYPES } from '../../../../../utils/constants'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import { Button, Checkbox, DialogContent, FormControlLabel, FormHelperText, MenuItem, Select, Switch, useTheme } from '@mui/material'
import SingleSelect from '../../../../../components/muiComponents/singleSelect'
import { Box } from '@mui/system'
import FlagKz from '../../../../../assets/img/flag-kz.png'
import FlagRu from '../../../../../assets/img/flag-ru.png'
import { CourseState } from '../../../../../features/types'
import { H3 } from '../../../../../layouts/TextStyles'
import CustomInput from '../../../../../components/muiComponents/customInput'
import { CustomRichTextEditor } from '../../../../../components/customRichTextEditor'
import { tone, ui } from '../../../../../utils/uiTokens'
import { courseSubjectApiSlice } from '../../../../../features/course/courseSubjectSlice'

const STRICT_MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

interface Props {
    onImageKzChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    onImageRuChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    formik: any
    imageKz: string
    imageRu: string
    editCourse?: CourseState
}

const AddCourseContent: React.FC<Props> = ({
    onImageKzChange,
    onImageRuChange,
    formik,
    imageKz,
    imageRu,
    editCourse,
}) => {
    const theme = useTheme()
    const { formatMessage } = useIntl()
    const { data: courseSubjects = [] } = courseSubjectApiSlice.useFetchActiveCourseSubjectsQuery(editCourse?.id, {
        skip: !editCourse?.id,
    })
    const freeIds: number[] = formik.values.freeSubjectIds || []
    const toggleStrict = (subjectId: number, strict: boolean) =>
        formik.setFieldValue(
            'freeSubjectIds',
            strict ? freeIds.filter((x) => x !== subjectId) : [...freeIds, subjectId],
        )
    const styles = {
        boxContainer: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 8,
        },
        textEditor: {
            minWidth: '400px',
            width: '100%',
            minHeight: '300px',
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: '8px',
            mt: 1,
            mb: 1,
        },
        error: {
            color: tone.bad.text,
            marginLeft: '12px',
        },
        img: {
            color: ui.pink,
            width: '48px',
            height: '48px',
        },
        flag: {
            width: 24,
            height: 24,
            marginLeft: 10,
            marginTop: 6,
        },
        titleBox: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
        },
    }

    const uploadImageKz = () => (
        <Box>
            <Button
                component="label"
                variant="outlined"
                color="primary"
                startIcon={<FileUploadIcon />}
            >
                Сурет жүктеу
                <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={onImageKzChange}
                />
            </Button>

            <FormHelperText style={styles.error}>
                {formik.touched.imageKz && formik.errors.imageKz}
            </FormHelperText>

            {formik.values.imageKz && (
                <ImageDiv>
                    <img
                        src={imageKz}
                        alt="flag"
                        style={styles.img}
                    />

                    <span>
                        {editCourse?.imageKz ?? formik.values.imageKz['name']}
                    </span>
                </ImageDiv>
            )}
        </Box>
    )

    const uploadImageRu = () => (
        <Box>
            <Button
                component="label"
                variant="outlined"
                color="primary"
                startIcon={<FileUploadIcon />}
            >
                Загрузить фото
                <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={onImageRuChange}
                />
            </Button>

            <FormHelperText style={styles.error}>
                {formik.touched.imageRu && formik.errors.imageRu}
            </FormHelperText>

            {formik.values.imageRu && (
                <ImageDiv>
                    <img
                        src={imageRu}
                        alt="flag"
                        style={styles.img}
                    />

                    <span>
                        {editCourse?.imageRu ?? formik.values.imageRu['name']}
                    </span>
                </ImageDiv>
            )}
        </Box>
    )

    return (
        <DialogContent>
            <form>
                <Box sx={styles.boxContainer}>
                    <Box
                        display={'flex'}
                        flexDirection="column"
                        minWidth={500}
                    >
                        <Box sx={{ height: '160px' }}>
                            <Box sx={styles.titleBox}>
                                <H3>Курс аты</H3>

                                <img
                                    alt="flag"
                                    src={FlagKz}
                                    style={styles.flag}
                                />
                            </Box>

                            <CustomInput
                                id="titleKz"
                                name="titleKz"
                                value={formik.values.titleKz}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.titleKz)}
                                helperText={formik.errors.titleKz}
                                placeholder="Enter name"
                                fullWidth
                            />
                        </Box>

                        <Box mb={2}>
                            <H3>Курс сипаттамасы</H3>

                            <Box
                                className="CustomEditor"
                                sx={styles.textEditor}
                            >
                                <CustomRichTextEditor
                                    value={formik.values.descriptionKz}
                                    onEditorChange={(text) => {
                                        formik.handleChange({
                                            target: {
                                                name: `descriptionKz`,
                                                value: text,
                                            },
                                        })
                                    }}
                                    inline
                                    editable
                                />
                            </Box>

                            <FormHelperText sx={styles.error}>
                                {formik.errors.descriptionKz}
                            </FormHelperText>
                        </Box>

                        {uploadImageKz()}

                        <Box sx={{ height: '160px', mt: 4 }}>
                            <Box sx={styles.titleBox}>
                                <H3>{formatMessage({id: "courseType"})}</H3>
                            </Box>

                            <SingleSelect
                                id="courseType"
                                name="courseType"
                                placeholder={formatMessage({
                                    id: 'courseType',
                                })}
                                value={formik.values.courseType}
                                menuItems={COURSE_TYPES}
                                handleChange={formik.handleChange}
                                handleBlur={formik.handleBlur}
                                handleError={Boolean(
                                    formik.errors.courseType,
                                )}
                            />

                            <FormHelperText style={styles.error}>
                                {formik?.touched?.courseType && formik?.errors?.courseType}
                            </FormHelperText>
                        </Box>
                    </Box>

                    <Box
                        display={'flex'}
                        flexDirection="column"
                        minWidth={500}
                    >
                        <Box sx={{ height: '160px' }}>
                            <Box sx={styles.titleBox}>
                                <H3>Название курса</H3>

                                <img
                                    alt="flag"
                                    src={FlagRu}
                                    style={styles.flag}
                                />
                            </Box>

                            <CustomInput
                                id="titleRu"
                                name="titleRu"
                                value={formik.values.titleRu}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.titleRu)}
                                helperText={formik.errors.titleRu}
                                placeholder="Enter name"
                                fullWidth
                            />
                        </Box>

                        <Box mb={2}>
                            <H3>Описание курса</H3>

                            <Box
                                className="CustomEditor"
                                sx={styles.textEditor}
                            >
                                <CustomRichTextEditor
                                    value={formik.values.descriptionRu}
                                    onEditorChange={(text) => {
                                        formik.handleChange({
                                            target: {
                                                name: `descriptionRu`,
                                                value: text,
                                            },
                                        })
                                    }}
                                    inline
                                    editable
                                />
                            </Box>

                            <FormHelperText sx={styles.error}>
                                {formik.errors.descriptionRu}
                            </FormHelperText>
                        </Box>

                        {uploadImageRu()}

                        <Box sx={{ height: '160px', mt: 4 }}>
                            <Box sx={styles.titleBox}>
                                <H3>{formatMessage({id: "videoCourseType"})}</H3>
                            </Box>

                            <SingleSelect
                                id="videoCourseType"
                                name="videoCourseType"
                                placeholder={formatMessage({
                                    id: 'videoCourseType',
                                })}
                                value={formik.values.videoCourseType}
                                menuItems={VIDEO_COURSE_TYPES}
                                handleChange={formik.handleChange}
                                handleBlur={formik.handleBlur}
                                handleError={Boolean(
                                    formik?.errors?.videoCourseType,
                                )}
                            />

                            <FormHelperText style={styles.error}>
                                {formik?.touched?.videoCourseType && formik?.errors?.videoCourseType}
                            </FormHelperText>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ mt: 3 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                name="sequentialLearningEnabled"
                                checked={Boolean(formik.values.sequentialLearningEnabled)}
                                onChange={(e) =>
                                    formik.setFieldValue('sequentialLearningEnabled', e.target.checked)
                                }
                            />
                        }
                        label={formatMessage({ id: 'sequentialLearning' })}
                    />
                    <FormHelperText>
                        {formatMessage({ id: 'sequentialLearningHint' })}
                    </FormHelperText>

                    {formik.values.sequentialLearningEnabled && (
                        <Box sx={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ fontWeight: 600, fontSize: '14px', mb: 1 }}>{formatMessage({ id: 'strictFrom' })}</Box>
                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                <Select
                                    size="small"
                                    displayEmpty
                                    value={formik.values.strictFromMonth || ''}
                                    onChange={(e) => {
                                        const month = Number(e.target.value) || null
                                        formik.setFieldValue('strictFromMonth', month)
                                        formik.setFieldValue('strictFromWeek', month ? formik.values.strictFromWeek || 1 : null)
                                    }}
                                    sx={{ minWidth: 200 }}
                                >
                                    <MenuItem value="">{formatMessage({ id: 'strictFromStart' })}</MenuItem>
                                    {STRICT_MONTHS.map((m) => (
                                        <MenuItem key={m} value={m}>{m} {formatMessage({ id: 'month' })}</MenuItem>
                                    ))}
                                </Select>
                                {!!formik.values.strictFromMonth && (
                                    <Select
                                        size="small"
                                        value={formik.values.strictFromWeek || 1}
                                        onChange={(e) => formik.setFieldValue('strictFromWeek', Number(e.target.value))}
                                        sx={{ minWidth: 140 }}
                                    >
                                        {[1, 2, 3, 4].map((w) => (
                                            <MenuItem key={w} value={w}>{w} {formatMessage({ id: 'week' })}</MenuItem>
                                        ))}
                                    </Select>
                                )}
                            </Box>
                            <FormHelperText>{formatMessage({ id: 'strictFromHint' })}</FormHelperText>
                        </Box>
                        {courseSubjects.length > 0 && (
                            <Box sx={{ mt: 2, minWidth: 260 }}>
                                <Box sx={{ fontWeight: 600, fontSize: '14px', mb: 1 }}>{formatMessage({ id: 'strictSubjects' })}</Box>
                                {courseSubjects.map((cs) => (
                                    <FormControlLabel
                                        key={cs.id}
                                        sx={{ display: 'flex', ml: 0 }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={!freeIds.includes(cs.subject.id)}
                                                onChange={(e) => toggleStrict(cs.subject.id, e.target.checked)}
                                            />
                                        }
                                        label={cs.titleKz}
                                    />
                                ))}
                                <FormHelperText>{formatMessage({ id: 'strictSubjectsHint' })}</FormHelperText>
                            </Box>
                        )}
                        </Box>
                    )}
                </Box>
            </form>
        </DialogContent>
    )
}

export default AddCourseContent

const ImageDiv = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`
